import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import {
  getAllCoupons,
  createCoupon,
  updateCoupon,
  getCouponById,
  getUsedCoupons,
  assignCoupon,
  getAllCouponsSummary,
  type UpdateCouponPayload,
  type UpdateCouponResponse,
  type CreateCouponPayload,
  type CreateCouponResponse,
  type AssignCouponResponse,
} from "../../apis/coupons";
import { Coupon } from "./types/couponTypes";
import { ListCouponsQuery } from "./types/couponListTypes";

interface CouponState {
  couponsSummary: any;
  coupons: any;
  usedCoupons: any;
  selectedCoupon: Coupon | null;
  loading: boolean;
  error: string | null;
  creating: boolean;
  updating: boolean;
  assigningCoupon: boolean;
}

const initialState: CouponState = {
  couponsSummary: null,
  coupons: null,
  usedCoupons: null,
  selectedCoupon: null,
  loading: false,
  error: null,
  creating: false,
  assigningCoupon: false,
  updating: false,
};

// Async Thunks
export const fetchAllCouponsSummary = createAsyncThunk(
  "coupon/fetchAllCouponsSummary",
  async (_, { rejectWithValue }) => {
    try {
      const res = await getAllCouponsSummary();
      return res?.data;
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data?.message || "Failed to fetch coupons summary"
      );
    }
  }
);

export const fetchAllCoupons = createAsyncThunk(
  "coupon/fetchAllCoupons",
  async (payload: ListCouponsQuery, { rejectWithValue }) => {
    try {
      const res = await getAllCoupons(payload);
      return res;
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data?.message || "Failed to fetch coupons"
      );
    }
  }
);

export const fetchAllUsedCoupons = createAsyncThunk(
  "coupon/fetchAllUsedCoupons",
  async (payload: ListCouponsQuery, { rejectWithValue }) => {
    try {
      const res = await getUsedCoupons(payload);
      return res;
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data?.message || "Failed to fetch used coupons"
      );
    }
  }
);

export const createNewCoupon = createAsyncThunk<
  CreateCouponResponse,
  CreateCouponPayload,
  { rejectValue: string }
>("coupon/createNewCoupon", async (payload, { rejectWithValue }) => {
  try {
    const res = await createCoupon(payload);
    return res;
  } catch (error: any) {
    return rejectWithValue(
      error?.response?.data?.message || "Failed to create coupon"
    );
  }
});

export const editCoupon = createAsyncThunk<
  UpdateCouponResponse,
  { couponId: string; payload: UpdateCouponPayload },
  { rejectValue: string }
>("coupon/editCoupon", async ({ couponId, payload }, { rejectWithValue }) => {
  try {
    const res = await updateCoupon(couponId, payload);
    return res;
  } catch (error: any) {
    return rejectWithValue(
      error?.response?.data?.message || "Failed to update coupon"
    );
  }
});

export const assignCouponToUsers = createAsyncThunk<
  AssignCouponResponse,
  { couponCode: string; assignedUserId: string[] },
  { rejectValue: string }
>(
  "coupon/assignCoupon",
  async ({ couponCode, assignedUserId }, { rejectWithValue }) => {
    try {
      const res = await assignCoupon(couponCode, { assignedUserId });
      return res;
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data?.message || "Failed to assign coupon"
      );
    }
  }
);

export const fetchSingleCoupon = createAsyncThunk(
  "coupon/fetchSingleCoupon",
  async (couponId: string, { rejectWithValue }) => {
    try {
      const res = await getCouponById(couponId);
      return res.data;
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data?.message || "Failed to fetch coupon"
      );
    }
  }
);

// Slice
const couponSlice = createSlice({
  name: "coupon",
  initialState,
  reducers: {
    resetCouponState: (state) => {
      state.coupons = null;
      state.selectedCoupon = null;
      state.loading = false;
      state.creating = false;
      state.updating = false;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Summary
      .addCase(fetchAllCouponsSummary.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAllCouponsSummary.fulfilled, (state, action) => {
        state.loading = false;
        state.couponsSummary = action.payload;
      })
      .addCase(
        fetchAllCouponsSummary.rejected,
        (state, action: PayloadAction<any>) => {
          state.loading = false;
          state.error = action.payload;
        }
      )
      // Fetch All
      .addCase(fetchAllCoupons.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAllCoupons.fulfilled, (state, action) => {
        state.loading = false;
        state.coupons = action.payload;
      })
      .addCase(
        fetchAllCoupons.rejected,
        (state, action: PayloadAction<any>) => {
          state.loading = false;
          state.error = action.payload;
        }
      )

      // Fetch Used Coupons
      .addCase(fetchAllUsedCoupons.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAllUsedCoupons.fulfilled, (state, action) => {
        state.loading = false;
        state.usedCoupons = action.payload;
      })
      .addCase(
        fetchAllUsedCoupons.rejected,
        (state, action: PayloadAction<any>) => {
          state.loading = false;
          state.error = action.payload;
        }
      )

      // Create
      .addCase(createNewCoupon.pending, (state) => {
        state.creating = true;
        state.error = null;
      })
      .addCase(createNewCoupon.fulfilled, (state) => {
        state.creating = false;
      })
      .addCase(createNewCoupon.rejected, (state, action) => {
        state.creating = false;
        state.error = (action.payload as string) ?? null;
      })

      // Update
      .addCase(editCoupon.pending, (state) => {
        state.updating = true;
        state.error = null;
      })
      .addCase(editCoupon.fulfilled, (state) => {
        state.updating = false;
      })
      .addCase(editCoupon.rejected, (state, action) => {
        state.updating = false;
        state.error = (action.payload as string) ?? null;
      })

      // assign
      .addCase(assignCouponToUsers.pending, (state) => {
        state.assigningCoupon = true;
        state.error = null;
      })
      .addCase(assignCouponToUsers.fulfilled, (state) => {
        state.assigningCoupon = false;
      })
      .addCase(assignCouponToUsers.rejected, (state, action) => {
        state.assigningCoupon = false;
        state.error = (action.payload as string) ?? null;
      })

      // Fetch One
      .addCase(fetchSingleCoupon.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchSingleCoupon.fulfilled,
        (state, action: PayloadAction<Coupon>) => {
          state.loading = false;
          state.selectedCoupon = action.payload;
        }
      )
      .addCase(
        fetchSingleCoupon.rejected,
        (state, action: PayloadAction<any>) => {
          state.loading = false;
          state.error = action.payload;
        }
      );
  },
});

export const { resetCouponState } = couponSlice.actions;
export default couponSlice.reducer;
